import type { StepStatus } from '@/lib/journey'
import type { BoardStatusFilter } from '@/lib/board/boardFilters'
import type { ProcedureResourceSummary, ProcedureStatus, ProcedureSummaryStep, ProcedureType } from '@/types/cgt'

/** Where a run stands, as the board filters it. */
export type BoardStatus = 'not_started' | 'in_progress' | 'pending_review' | 'expired' | 'complete' | 'cancelled'

/** The Expired badge: a cleaning task not done on its day (z8nrz7f9u5). */
export const EXPIRED_BADGE = { label: 'Expired', dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200' }

/**
 * A cancelled run is cancelled whatever its steps say; otherwise the shared
 * step status decides (see `deriveProcedureStepStatus`).
 */
export function boardStatusOf(procedureStatus: ProcedureStatus, stepStatus: StepStatus): BoardStatus {
  if (procedureStatus === 'cancelled') return 'cancelled'
  if (stepStatus === 'completed') return 'complete'
  if (stepStatus === 'in_progress') return 'in_progress'
  if (stepStatus === 'pending_review') return 'pending_review'
  return 'not_started'
}

export function matchesBoardStatus(status: BoardStatus, filter: BoardStatusFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'open') {
    return status === 'not_started' || status === 'in_progress' || status === 'pending_review' || status === 'expired'
  }
  return status === filter
}

/**
 * Board order: the most urgent due date first, runs with no due date after
 * them, newest start first within each.
 */
export function compareBoardCards(
  a: { dueDate: string | null; startedAt: string; id: string },
  b: { dueDate: string | null; startedAt: string; id: string },
): number {
  if (a.dueDate !== b.dueDate) {
    if (!a.dueDate) return 1
    if (!b.dueDate) return -1
    if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1
  }
  if (a.startedAt !== b.startedAt) return a.startedAt < b.startedAt ? 1 : -1
  return a.id.localeCompare(b.id)
}

export interface LeadItem {
  label: string
  value: string
}

/**
 * The identifying line under a card's header, which depends on the type: a
 * transplant names its patient, a kit run its kit, an MES order its MFG number
 * and quantity. The work order follows wherever one exists.
 *
 * A kit run's `patientMrn` holds its supply id, not a patient.
 */
export function leadItemsFor(run: {
  type: ProcedureType
  patientMrn: string | null
  patientName: string | null
  mfgNumber: string | null
  quantity: number
  workOrderNumber: string | null
}): LeadItem[] {
  const items: LeadItem[] = []
  if (run.type === 'KitCreation') {
    if (run.patientMrn) items.push({ label: 'Kit ID', value: run.patientMrn })
  } else if (run.type === 'MES') {
    // Null only on an order created before MFG numbers existed.
    if (run.mfgNumber) items.push({ label: 'MFG', value: run.mfgNumber })
    items.push({ label: 'QTY', value: String(run.quantity) })
  } else if (run.patientMrn) {
    items.push({
      label: 'Patient',
      value: run.patientName ? `${run.patientName} (${run.patientMrn})` : run.patientMrn,
    })
  }
  if (run.workOrderNumber) items.push({ label: 'WO', value: run.workOrderNumber })
  return items
}

/**
 * Inventory icon: hidden when the run needs no inventory, held (green) when
 * every requirement line is held, missing (red) otherwise.
 */
export function inventoryIconState(summary: ProcedureResourceSummary | undefined): 'hidden' | 'held' | 'missing' {
  if (!summary || summary.required === 0) return 'hidden'
  return summary.reserved >= summary.required ? 'held' : 'missing'
}

/**
 * Equipment icon: hidden when the run needs no equipment, held (green) when
 * every requirement is held, neutral otherwise.
 */
export function equipmentIconState(summary: ProcedureResourceSummary | undefined): 'hidden' | 'held' | 'pending' {
  if (!summary || summary.required === 0) return 'hidden'
  return summary.reserved >= summary.required ? 'held' : 'pending'
}

/** Hover text for the inventory and equipment status icons. */
export function resourceIconTooltip(
  kind: 'Inventory' | 'Equipment',
  summary: ProcedureResourceSummary,
  canReserve: boolean,
): string {
  const { required, reserved } = summary
  const items = required === 1 ? 'item' : 'items'
  if (reserved >= required) return `${kind} reserved: all ${required} ${items}.`
  const status = `${kind} not reserved: ${reserved} of ${required} ${items} reserved.`
  return canReserve ? `${status} Click Reserve to reserve.` : status
}

/** The step a run is on: the first in progress, else the first not started. */
export function currentStepName(steps: ProcedureSummaryStep[] | undefined): string | null {
  if (!steps) return null
  const step = steps.find((s) => s.status === 'in_progress') ?? steps.find((s) => s.status === 'not_started')
  return step?.name ?? null
}

/** Reserve is offered while a run still needs something it does not hold. */
export function needsReservation(
  status: BoardStatus,
  inventory: ProcedureResourceSummary | undefined,
  equipment: ProcedureResourceSummary | undefined,
): boolean {
  if (status === 'complete' || status === 'cancelled') return false
  const short = (s: ProcedureResourceSummary | undefined) => !!s && s.required > 0 && s.reserved < s.required
  return short(inventory) || short(equipment)
}

/** Cards per row: the chosen count, reduced until each card keeps a readable width. */
export function effectiveColumns(chosen: number, widthPx: number, minCardPx = 260): number {
  if (widthPx <= 0) return Math.max(1, chosen)
  return Math.max(1, Math.min(chosen, Math.floor(widthPx / minCardPx)))
}

/** `2026-09-09T…` → `Sep 9`, the header's short due date. */
export function shortDueDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}
